import React from 'react';
import './DateRangesReport.scss';

const DateRangesReport = ({ communities, dates, getDayStatus }) => {
    const generateRanges = (statusType) => {
        const ranges = [];
        communities.forEach(community => {
            const communityRanges = [];
            let startDate = null;
            let endDate = null;
            
            dates.forEach(date => {
                const status = getDayStatus(community.id, date);
                const hasStatus = status === statusType;
                
                if (hasStatus && startDate === null) {
                    startDate = date;
                    endDate = date;
                } else if (hasStatus && startDate !== null) {
                    endDate = date;
                } else if (!hasStatus && startDate !== null) {
                    communityRanges.push({
                        start: startDate,
                        end: endDate,
                        days: Math.floor((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1
                    });
                    startDate = null;
                    endDate = null;
                }
            });

            if (startDate !== null) {
                communityRanges.push({
                    start: startDate,
                    end: endDate,
                    days: Math.floor((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1
                });
            }

            if (communityRanges.length > 0) {
                ranges.push({ community, ranges: communityRanges });
            }
        });
        return ranges;
    };

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString('ru-RU');

    const getStatusConfig = (statusType) => {
        switch (statusType) {
            case 'single':
                return { title: 'Данные есть', color: 'green' };
            case 'empty':
                return { title: 'Данных нет', color: 'gray' };
            case 'duplicate':
                return { title: 'Есть дубли', color: 'red' };
            default:
                return { title: 'Неизвестно', color: 'gray' };
        }
    };

    const renderStatusSection = (statusType) => {
        const ranges = generateRanges(statusType);
        const config = getStatusConfig(statusType);
        
        if (ranges.length === 0) {
            return (
                <div className={`report-section report-${config.color}`}>
                    <h4 className="section-title">{config.title}</h4>
                    <p className="no-data">Нет данных для отображения</p>
                </div>
            );
        }

        return (
            <div className={`report-section report-${config.color}`}>
                <h4 className="section-title">
                    {config.title} ({ranges.length} групп)
                </h4>
                <div className="section-body">
                    {ranges.map((item, index) => (
                        <div key={`${item.community.id}-${index}`} className="community-block">
                            <div className="community-header">
                                <span className="community-name">{item.community.name}</span>
                                <span className="community-id">ID: {item.community.id}</span>
                            </div>
                            <div className="range-list">
                                {item.ranges.map((range, i) => (
                                    <div key={i} className="range-item">
                                        <span>{formatDate(range.start)} — {formatDate(range.end)}</span>
                                        <span>{range.days} {range.days === 1 ? 'день' : range.days < 5 ? 'дня' : 'дней'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="report-wrapper">
            <div className="report-block">
                <div className="report-header">
                    <h4>Отчет по группам</h4>
                </div>
                <div className="report-content">
                    {communities.map(community => {
                        const communityData = { single: [], empty: [], duplicate: [] };
                        
                        dates.forEach(date => {
                            const status = getDayStatus(community.id, date);
                            if (status === 'single') communityData.single.push(date);
                            else if (status === 'empty') communityData.empty.push(date);
                            else if (status === 'duplicate') communityData.duplicate.push(date);
                        });
                        
                        const totalDays = dates.length;
                        const dataDays = communityData.single.length + communityData.duplicate.length;
                        const emptyDays = communityData.empty.length;
                        const duplicateDays = communityData.duplicate.length;

                        return (
                            <div key={community.id} className="community-report">
                                <div className="community-report-header">
                                    <div>
                                        <h5>{community.name}</h5>
                                        <p>ID: {community.id}</p>
                                    </div>
                                    <div className="stats">
                                        <div>Всего дней: {totalDays}</div>
                                        <div>С данными: {dataDays} ({Math.round(dataDays/totalDays*100)}%)</div>
                                    </div>
                                </div>
                                
                                <div className="status-grid">
                                    <div className="status-card status-green">
                                        <div className="status-header">
                                            <span>Данные есть</span>
                                            <span>{communityData.single.length}</span>
                                        </div>
                                        <div className="status-dates">
                                            {communityData.single.length > 0 ? 
                                                `${formatDate(communityData.single[0])} — ${formatDate(communityData.single.at(-1))}` :
                                                'Нет данных'}
                                        </div>
                                    </div>

                                    <div className="status-card status-gray">
                                        <div className="status-header">
                                            <span>Нет данных</span>
                                            <span>{emptyDays}</span>
                                        </div>
                                        <div className="status-dates">
                                            {emptyDays > 0 ? 
                                                `${formatDate(communityData.empty[0])} — ${formatDate(communityData.empty.at(-1))}` :
                                                'Все дни заполнены'}
                                        </div>
                                    </div>

                                    <div className="status-card status-red">
                                        <div className="status-header">
                                            <span>Есть дубли</span>
                                            <span>{duplicateDays}</span>
                                        </div>
                                        <div className="status-dates">
                                            {duplicateDays > 0 ? 
                                                `${formatDate(communityData.duplicate[0])} — ${formatDate(communityData.duplicate.at(-1))}` :
                                                'Дубли не найдены'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="report-detailed">
                <h4>Детальный отчет по типам данных</h4>
                {renderStatusSection('single')}
                {renderStatusSection('empty')}
                {renderStatusSection('duplicate')}
            </div>
        </div>
    );
};

export default DateRangesReport;
